import { Bell, ChevronRight, PenSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, PageHeader } from '../components/ui';
import { useCitizenWorkspaceId, useCitizenOutreachItems, type OutreachItem } from '../hooks/useCitizenData';
import { daysUntil } from '../lib/utils';

export default function RequestsListScreen() {
  const navigate = useNavigate();
  const workspaceId = useCitizenWorkspaceId();
  const { data: items, isLoading } = useCitizenOutreachItems(workspaceId);

  const active = (items || []).filter((o) => {
    const s = (o.status || '').toLowerCase();
    return s === 'active' || s === 'launched';
  });

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Open Requests" onBack={() => navigate('/citizen/home')} />

      <div className="px-6 pt-2 pb-24">
        <p className="text-sm text-[color:var(--muted-foreground)] leading-relaxed mb-5">
          Surveys and event registrations from organizations and legislators you follow. Your responses are routed
          through the Citizen Standard.
        </p>

        {isLoading && <p className="text-sm text-[color:var(--muted-foreground)] py-6">Loading…</p>}

        {!isLoading && active.length === 0 && (
          <Card
            className="p-5 flex items-center gap-3 border-0"
            style={{ background: 'transparent', borderStyle: 'dashed', borderWidth: 1, borderColor: 'var(--border)' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'var(--accent)', color: 'var(--primary)' }}
            >
              <PenSquare className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">No open requests</p>
              <p className="text-xs text-[color:var(--muted-foreground)]">
                When a group needs your voice, it'll show up here.
              </p>
            </div>
          </Card>
        )}

        <div className="space-y-3">
          {active.map((r) => (
            <RequestCard key={r.id} r={r} onClick={() => navigate(`/citizen/requests/${r.id}`)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RequestCard({ r, onClick }: { r: OutreachItem; onClick: () => void }) {
  const expires = r.closes_at || r.end_date;
  const expDays = expires ? daysUntil(expires) : null;
  const urgent = expDays !== null && expDays <= 3;
  const isSurvey = (r.type || '').toLowerCase() !== 'rsvp';

  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-2xl text-left hover-elevate active-elevate-2 border block"
      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'var(--hero-deep-3)', color: 'white' }}
        >
          <PenSquare className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={isSurvey ? 'gold' : 'accent'}>{isSurvey ? 'Survey' : 'RSVP'}</Badge>
          </div>
          <h4 className="text-base font-bold leading-tight">{r.name}</h4>
          {r.description && (
            <p className="text-xs text-[color:var(--muted-foreground)] mt-1 leading-snug line-clamp-2">
              {r.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
            {r.template_name && (
              <span className="text-[11px] font-semibold text-[color:var(--muted-foreground)]">
                From <span style={{ color: 'var(--foreground)' }}>{r.template_name}</span>
              </span>
            )}
            {expDays !== null && (
              <span
                className="text-[11px] font-bold ml-auto flex items-center gap-1"
                style={{ color: urgent ? '#C24A4A' : 'var(--muted-foreground)' }}
              >
                {urgent && <Bell className="w-3 h-3" />}
                {expDays} day{expDays === 1 ? '' : 's'} left
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)]" />
          </div>
        </div>
      </div>
    </button>
  );
}
